import { useState } from "react";
import { AlertTriangle, Home, School, Building2, Send } from "lucide-react";
import { toast } from "sonner";
import { ChapterHeading, Reveal } from "./Reveal";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FAMILY_IMG =
  "https://images.unsplash.com/photo-1587235587178-e4a6dbe63726?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHw0fHxwYXJlbnQlMjBjaGlsZCUyMGxhdWdoaW5nfGVufDB8fHx8MTc4NjcxNzQyMXww&ixlib=rb-4.1.0&q=85";

const SETTINGS = [
  { icon: Home, label: "In Home" },
  { icon: School, label: "In School" },
  { icon: Building2, label: "In Clinic" },
];

const EMPTY = {
  parent_first_name: "",
  parent_last_name: "",
  phone: "",
  email: "",
  news_updates: false,
  preferred_contact: "",
  child_first_name: "",
  child_last_name: "",
  child_dob: "",
  child_age: "",
  diagnosis: "",
  school: "",
  insurance_provider: "",
  member_id: "",
  secondary_insurance: "",
  preferred_schedule: "",
  goals: "",
  referral_source: "",
};

const SERVICE_OPTIONS = ["Clinic-Based ABA", "Parent Training", "Social Skills", "Assessment", "Not Sure Yet"];
const DIAGNOSIS_OPTIONS = ["Autism Spectrum Disorder", "Developmental Delay", "Speech or Language Delay", "ADHD", "Other", "Not sure yet"];
const SOURCE_OPTIONS = ["Google Search", "Pediatrician / Physician", "School or Daycare", "Friend or Family", "Social Media", "Insurance Provider", "Other"];

export const Intake = () => {
  const [form, setForm] = useState(EMPTY);
  const [services, setServices] = useState([]);
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const toggleService = (s) =>
    setServices((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please agree to be contacted before submitting the intake form.");
      return;
    }
    setSending(true);
    try {
      await api.post("/intake", { ...form, service_needs: services.join(", ") });
      toast.success("Intake request sent. We'll be in touch soon.");
      setForm(EMPTY);
      setServices([]);
      setConsent(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="families" data-testid="families-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <ChapterHeading
          number="04"
          eyebrow="Patients & Families"
          title="Start your"
          accent="intake journey."
          testid="families-heading"
        />

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            <Reveal>
              <p className="text-base md:text-lg leading-relaxed text-brand-muted max-w-xl">
                Serving children and families in Houston with compassionate, evidence-based ABA
                therapy. Complete the form and our team will contact you soon.
              </p>
            </Reveal>

            <div className="mt-8 flex flex-wrap gap-4" data-testid="families-settings">
              {SETTINGS.map((s, i) => (
                <Reveal key={s.label} delay={0.08 * i}>
                  <div className="flex items-center gap-3 rounded-2xl border border-brand-lightblue/50 bg-white px-6 py-4 shadow-soft transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lift">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-pale text-brand-blue">
                      <s.icon className="h-5 w-5" />
                    </span>
                    <span className="font-serif text-xl font-semibold text-brand-blue">{s.label}</span>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.2} className="mt-10">
              <div className="relative max-w-md">
                <div className="absolute -top-4 -left-4 h-full w-full rounded-t-[999px] rounded-b-[2rem] border-2 border-brand-lightblue/70" aria-hidden="true" />
                <img
                  src={FAMILY_IMG}
                  alt="Parent and child sharing a joyful moment"
                  data-testid="families-image"
                  className="relative h-80 w-full object-cover rounded-t-[999px] rounded-b-[2rem] shadow-soft"
                />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <form
              id="intake"
              data-testid="intake-form"
              onSubmit={submit}
              className="rounded-[2rem] bg-white border border-brand-lightblue/40 shadow-lift p-8 md:p-10"
            >
              <h3 className="font-serif text-3xl font-semibold text-brand-blue mb-2">Family Intake Form</h3>
              <div
                data-testid="intake-warning"
                className="mt-4 mb-8 flex items-start gap-3 rounded-2xl bg-brand-pale/60 border border-brand-lightblue/50 px-5 py-4"
              >
                <AlertTriangle className="h-5 w-5 text-brand-blue shrink-0 mt-0.5" />
                <p className="text-sm leading-relaxed text-brand-ink">
                  This form does not transmit protected health information (PHI) by email. Our team
                  follows HIPAA guidelines when handling your information.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <p className="sm:col-span-2 text-xs uppercase tracking-[0.18em] font-semibold text-brand-coral pt-1">Parent / Guardian</p>
                <div className="space-y-2">
                  <Label htmlFor="wl-parent-first">First Name *</Label>
                  <Input id="wl-parent-first" data-testid="intake-parent-first-name" required value={form.parent_first_name} onChange={set("parent_first_name")} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wl-parent-last">Last Name *</Label>
                  <Input id="wl-parent-last" data-testid="intake-parent-last-name" required value={form.parent_last_name} onChange={set("parent_last_name")} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wl-phone">Phone *</Label>
                  <Input id="wl-phone" data-testid="intake-phone" type="tel" required value={form.phone} onChange={set("phone")} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wl-email">Email *</Label>
                  <Input id="wl-email" data-testid="intake-email" type="email" required value={form.email} onChange={set("email")} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label>Preferred Contact Method</Label>
                  <Select value={form.preferred_contact} onValueChange={set("preferred_contact")}>
                    <SelectTrigger data-testid="intake-preferred-contact" className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12">
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Call">Call</SelectItem>
                      <SelectItem value="Text">Text</SelectItem>
                      <SelectItem value="Email">Email</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>How Did You Hear About Us?</Label>
                  <Select value={form.referral_source} onValueChange={set("referral_source")}>
                    <SelectTrigger data-testid="intake-referral-source" className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12">
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      {SOURCE_OPTIONS.map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <label className="sm:col-span-2 flex items-center gap-3 cursor-pointer" data-testid="intake-news-label">
                  <input
                    type="checkbox"
                    data-testid="intake-news-checkbox"
                    checked={form.news_updates}
                    onChange={(e) => setForm((f) => ({ ...f, news_updates: e.target.checked }))}
                    className="h-4 w-4 shrink-0 accent-[#3A5A8C]"
                  />
                  <span className="text-sm text-brand-ink">Sign up for news and updates</span>
                </label>

                <p className="sm:col-span-2 text-xs uppercase tracking-[0.18em] font-semibold text-brand-coral pt-2">Child</p>
                <div className="space-y-2">
                  <Label htmlFor="wl-child-first">First Name *</Label>
                  <Input id="wl-child-first" data-testid="intake-child-first-name" required value={form.child_first_name} onChange={set("child_first_name")} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wl-child-last">Last Name *</Label>
                  <Input id="wl-child-last" data-testid="intake-child-last-name" required value={form.child_last_name} onChange={set("child_last_name")} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wl-dob">Date of Birth *</Label>
                  <Input id="wl-dob" data-testid="intake-child-dob" type="date" required value={form.child_dob} onChange={set("child_dob")} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wl-age">Child Age *</Label>
                  <Input id="wl-age" data-testid="intake-child-age" required value={form.child_age} onChange={set("child_age")} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label>Diagnosis</Label>
                  <Select value={form.diagnosis} onValueChange={set("diagnosis")}>
                    <SelectTrigger data-testid="intake-diagnosis" className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12">
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      {DIAGNOSIS_OPTIONS.map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wl-school">Current School / Daycare (optional)</Label>
                  <Input id="wl-school" data-testid="intake-school" value={form.school} onChange={set("school")} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
                </div>

                <p className="sm:col-span-2 text-xs uppercase tracking-[0.18em] font-semibold text-brand-coral pt-2">Insurance & Services</p>
                <div className="space-y-2">
                  <Label htmlFor="wl-insurance">Insurance Provider *</Label>
                  <Input id="wl-insurance" data-testid="intake-insurance-provider" required value={form.insurance_provider} onChange={set("insurance_provider")} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wl-member">Member ID (optional at first)</Label>
                  <Input id="wl-member" data-testid="intake-member-id" value={form.member_id} onChange={set("member_id")} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wl-secondary">Secondary Insurance (optional)</Label>
                  <Input id="wl-secondary" data-testid="intake-secondary-insurance" value={form.secondary_insurance} onChange={set("secondary_insurance")} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label>Preferred Schedule</Label>
                  <Select value={form.preferred_schedule} onValueChange={set("preferred_schedule")}>
                    <SelectTrigger data-testid="intake-preferred-schedule" className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12">
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mornings">Mornings</SelectItem>
                      <SelectItem value="Afternoons">Afternoons</SelectItem>
                      <SelectItem value="Full Day">Full Day</SelectItem>
                      <SelectItem value="Flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3 sm:col-span-2">
                  <Label>Service Needs — what services are you seeking?</Label>
                  <div className="flex flex-wrap gap-3" data-testid="intake-service-needs">
                    {SERVICE_OPTIONS.map((s) => (
                      <label
                        key={s}
                        className={`flex items-center gap-2.5 rounded-full border px-5 py-2.5 text-sm font-medium cursor-pointer transition-[background-color,border-color,color] duration-300 ${
                          services.includes(s)
                            ? "bg-brand-blue text-white border-brand-blue"
                            : "bg-white text-brand-blue border-brand-lightblue/60 hover:bg-brand-pale"
                        }`}
                      >
                        <input
                          type="checkbox"
                          data-testid={`intake-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          checked={services.includes(s)}
                          onChange={() => toggleService(s)}
                          className="hidden"
                        />
                        {s}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="wl-goals">Family Goals / Concerns</Label>
                  <Textarea
                    id="wl-goals"
                    data-testid="intake-goals"
                    rows={4}
                    placeholder="What are your biggest concerns or goals for your child? Examples: communication, behavior, potty training, school readiness, social skills"
                    value={form.goals}
                    onChange={set("goals")}
                    className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50"
                  />
                </div>

                <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer" data-testid="intake-consent-label">
                  <input
                    type="checkbox"
                    data-testid="intake-consent-checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 accent-[#3A5A8C]"
                  />
                  <span className="text-sm text-brand-ink leading-relaxed">
                    I agree to be contacted by Milestone Pediatric Therapy regarding services.
                  </span>
                </label>
              </div>

              <button
                type="submit"
                data-testid="intake-submit-button"
                disabled={sending}
                className="group mt-8 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-brand-coral text-white font-semibold px-10 py-4 shadow-soft transition-[transform,box-shadow,background-color,opacity] duration-300 hover:-translate-y-0.5 hover:shadow-lift hover:bg-[#f06666] disabled:opacity-60 disabled:translate-y-0"
              >
                {sending ? "Sending…" : "Send Intake Request"}
                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
